import React, { useEffect, useMemo, useState } from "react";
import { Box, Card, List, ListItemButton, ListItemText, Typography, Divider, Tooltip } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ReportProblemRoundedIcon from "@mui/icons-material/ReportProblemRounded";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";

const collectSections = (rootEl) => {
  if (!rootEl || typeof document === "undefined") return [];
  const nodes = Array.from(document.querySelectorAll("[id^='form-section-']"));
  return nodes
    .map((el) => ({
      id: el.id,
      title: el.getAttribute("data-section-title") || el.id,
      status: el.getAttribute("data-section-status") || "",
    }))
    .filter((s, idx, arr) => s.title && arr.findIndex((t) => t.id === s.id) === idx);
};

const FormQuickNav = ({ scrollContainerRef }) => {
  const [sections, setSections] = useState([]);
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    const container = scrollContainerRef?.current || null;
    setSections(collectSections(container));

    if (!container) return;

    const handleScroll = () => {
      const containerRect = container.getBoundingClientRect();
      const candidates = sections
        .map((s) => {
          const el = document.getElementById(s.id);
          if (!el) return null;
          const r = el.getBoundingClientRect();
          return { id: s.id, top: r.top - containerRect.top };
        })
        .filter(Boolean)
        .sort((a, b) => Math.abs(a.top) - Math.abs(b.top));
      if (candidates.length) setActiveId(candidates[0].id);
    };

    const resizeObserver = new ResizeObserver(() => setSections(collectSections(container)));
    resizeObserver.observe(container);

    const mutationObserver = new MutationObserver(() => setSections(collectSections(container)));
    mutationObserver.observe(container, { childList: true, subtree: true });

    container.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      container.removeEventListener("scroll", handleScroll);
      mutationObserver.disconnect();
      resizeObserver.disconnect();
    };
  }, [scrollContainerRef, /* re-run when titles list changes */ JSON.stringify(sections.map((s) => s.id))]);

  const handleClick = (id) => {
    const target = document.getElementById(id);
    if (!target) return;
    target.scrollIntoView({ behavior: "smooth", block: "start" });
    setActiveId(id);
  };

  if (!sections.length) return null;

  return (
    <Card
      sx={{
        boxShadow:
          "0px 1px 2px 0px rgba(16, 24, 40, 0.06), 0px 1px 3px 0px rgba(16, 24, 40, 0.1)",
        border: "1px solid rgba(234, 236, 240, 1)",
        borderRadius: "12px",
        p: 2,
        display: "flex",
        flexDirection: "column",
        maxHeight: "calc(100vh - 200px)",
        overflow: "hidden",
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#344054", mb: 1 }}>
        Quick Navigation
      </Typography>
      <Divider sx={{ mb: 1 }} />
      <List dense disablePadding sx={{ overflowY: "auto", flex: 1 }}>
        {sections.map((s) => {
          const isComplete = s.status === "completed";
          const hasNote = s.status === "incompletewithrequest" || s.status === "submitted_with_note";
          return (
            <ListItemButton
              key={s.id}
              selected={s.id === activeId}
              onClick={() => handleClick(s.id)}
              sx={{
                borderRadius: "8px",
                pl:0.5,
                mb: 0.5,
                '&.Mui-selected': { bgcolor: "action.selected" },
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Box sx={{ width: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1 }}>
                {isComplete ? (
                  <CheckCircleIcon sx={{ color: "#22c55e", fontSize: 16 }} />
                ) : hasNote ? (
                  <ReportProblemRoundedIcon sx={{ color: "#f59e0b", fontSize: 16 }} />
                ) : (
                  <RadioButtonUncheckedIcon sx={{ color: "#d1d5db", fontSize: 16 }} />
                )}
              </Box>
              <Tooltip title={s.title} placement="right">
                <ListItemText
                  primary={s.title}
                  primaryTypographyProps={{
                    sx: {
                      fontSize: 14,
                      fontWeight: 400,
                      color: "#1F2937",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "100%",
                      display: "block",
                    },
                  }}
                  sx={{ m: 0, minWidth: 0 }}
                />
              </Tooltip>
            </ListItemButton>
          );
        })}
      </List>
    </Card>
  );
};

export default FormQuickNav;


